<template>
  <div class="m7-panel">
    <div class="m7-panel__signature">{{ store.settings.timeSignature }}</div>

    <MetronomeBeatDots
      :accents="store.accents"
      :editable="true"
      class="mb-2"
      :aria-label="$t('metronome.accentPattern')"
      @cycle="store.cycleAccentAt($event)"
    />
    <p class="m7-panel__hint">{{ $t('metronome.accentHint') }}</p>

    <div class="m7-stepper">
      <v-btn icon="$minus" size="small" variant="text" :aria-label="$t('metronome.fewerBeats')" @click="step(-1)" />
      <span class="m7-stepper__value">{{ meter.numerator }}</span>
      <v-btn icon="$plus" size="small" variant="text" :aria-label="$t('metronome.moreBeats')" @click="step(1)" />
    </div>

    <div class="m7-stepper">
      <v-btn
        icon="$minus"
        size="small"
        variant="text"
        :aria-label="$t('metronome.shorterBeat')"
        @click="stepDenominator(-1)"
      />
      <span class="m7-stepper__value">{{ meter.denominator }}</span>
      <v-btn
        icon="$plus"
        size="small"
        variant="text"
        :aria-label="$t('metronome.longerBeat')"
        @click="stepDenominator(1)"
      />
    </div>

    <v-select
      :model-value="store.settings.subdivision"
      :items="subdivisionItems"
      :label="$t('metronome.subdivision')"
      density="compact"
      variant="outlined"
      hide-details
      class="mt-4"
      @update:model-value="store.update('subdivision', $event)"
    />

    <!-- Only 6/8, 9/8 and 12/8 have a meaningful compound reading. -->
    <v-switch
      v-if="canCompound"
      :model-value="store.settings.compound"
      :label="$t('metronome.compound')"
      color="primary"
      density="compact"
      hide-details
      class="mt-2"
      @update:model-value="store.setCompound($event)"
    />

    <v-btn variant="text" size="small" class="mt-2" @click="store.resetAccents()">
      {{ $t('metronome.resetAccents') }}
    </v-btn>
  </div>
</template>

<script setup>
import {
  DENOMINATORS,
  SUBDIVISIONS,
  formatTimeSignature,
  isCompoundCapable,
  parseTimeSignature,
  subdivisionKey,
} from '~/utils/metronomeMeter'

const { t } = useI18n()
const store = useMetronomeStore()

const meter = computed(() => parseTimeSignature(store.settings.timeSignature))
const canCompound = computed(() => isCompoundCapable(store.settings.timeSignature))

const subdivisionItems = computed(() =>
  SUBDIVISIONS.map((value) => ({ value, title: t(`metronome.subdivisions.${subdivisionKey(value)}`) }))
)

function step(delta) {
  const next = Math.min(Math.max(meter.value.numerator + delta, 1), 16)
  store.setTimeSignature(formatTimeSignature(next, meter.value.denominator))
}

function stepDenominator(delta) {
  const index = DENOMINATORS.indexOf(meter.value.denominator)
  const next = DENOMINATORS[Math.min(Math.max(index + delta, 0), DENOMINATORS.length - 1)]
  store.setTimeSignature(formatTimeSignature(meter.value.numerator, next))
}
</script>

<style scoped>
.m7-panel {
  text-align: center;
}

.m7-panel__signature {
  font-size: 2rem;
  font-weight: 300;
  letter-spacing: 0.04em;
  margin-bottom: 0.75rem;
}

.m7-panel__hint {
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  color: var(--m7-muted);
  margin-bottom: 0.5rem;
}

.m7-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 0.25rem 0;
  border-top: 1px solid var(--m7-tick);
}

.m7-stepper__value {
  font-size: 2rem;
  font-weight: 300;
  min-width: 2.5rem;
}
</style>
